import { createFileRoute, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { Hero } from "@/components/home/Hero";
import { HomePromotions } from "@/components/home/HomePromotions";
import { ShopByVideo } from "@/components/home/ShopByVideo";
import { BrandStory } from "@/components/home/BrandStory";
import { Collections } from "@/components/home/Collections";
import { ProductGrid } from "@/components/home/ProductGrid";
import { WhyCB16 } from "@/components/home/WhyCB16";
import { Testimonials } from "@/components/home/Testimonials";
import { DiscoveryPacks } from "@/components/home/DiscoveryPacks";
import { Newsletter } from "@/components/home/Newsletter";
import { SocialShowcase } from "@/components/home/SocialShowcase";
import { CallToAction } from "@/components/home/CallToAction";
import { JournalTeasers } from "@/components/home/JournalTeasers";
import { getCatalogRepository } from "@/lib/catalog/repository";
import type { Product } from "@/components/home/product-data";
import { startingPrice } from "@/lib/catalog/size-pricing";
import { publishedCmsPageQuery } from "@/lib/cms/queries";
import { publicPacksQuery } from "@/lib/packs/queries";
import { useHomeSection } from "@/lib/cms/content";
import { canonicalUrl } from "@/lib/seo/site";
import { ldScript, organizationSchema, webSiteSchema } from "@/lib/seo/schema";


export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    const repo = getCatalogRepository();
    const [products, collections] = await Promise.all([
      repo.getProducts(),
      repo.getCollections(),
      // Section copy is edited in Admin → CMS Pages; prefetch it so the server
      // and the client render exactly the same content.
      context.queryClient.ensureQueryData(publishedCmsPageQuery("home")),
      // Discovery Packs rail — same public pack module used by /packs.
      context.queryClient.ensureQueryData(publicPacksQuery()),
    ]);
    const map = (items: typeof products): Product[] => items.map((p) => ({
      id: p.id, slug: p.slug, name: p.name, family: p.family, notes: p.notes,
      price: `Starting From ${new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(startingPrice(p))}`,
      img: p.img, badge: p.badge,
    }));
    // Homepage rails are curated from Admin → Products via the
    // "Show in Best Sellers" / "Show in New Arrivals" checkboxes.
    // Until an administrator ticks any product, the previous automatic
    // behaviour is preserved so the live site does not change.
    const curatedBest = products.filter((p) => p.showInBestSellers);
    const curatedNew = products.filter((p) => p.showInNewArrivals);
    return {
      bestSellers: map(
        curatedBest.length > 0 ? curatedBest : products.filter((p) => p.badge === "Bestseller"),
      ),
      newArrivals: map(
        curatedNew.length > 0
          ? curatedNew
          : [...products].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      ),
      collections,
      imagery: products.map((p) => p.img).filter(Boolean),
    };
  },
  head: () => ({
    meta: [
      { title: "CB16 Maison — Rare Luxury Perfumery, Hand-Blended in Mumbai" },
      {
        name: "description",
        content:
          "CB16 is an independent luxury perfume maison crafting hand-blended fragrances from rare naturals. Discover signature scents, limited flacons, and private consultations.",
      },
      { property: "og:title", content: "CB16 Maison — Rare Luxury Perfumery" },
      {
        property: "og:description",
        content:
          "Hand-blended in small batches at the CB16 atelier in Mumbai, India. Discover the CB16 collection.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonicalUrl("/") },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "CB16 Maison — Rare Luxury Perfumery" },
      {
        name: "twitter:description",
        content:
          "Hand-blended in small batches at the CB16 atelier in Mumbai, India. Discover the CB16 collection.",
      },
    ],
    links: [{ rel: "canonical", href: canonicalUrl("/") }],
    scripts: [ldScript(organizationSchema()), ldScript(webSiteSchema())],
  }),

  // A dropped catalog request must never leave a blank homepage: show a
  // recoverable message that re-runs the existing loader on retry.
  errorComponent: HomeLoadError,
  component: Index,
});

function HomeLoadError({ reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <section className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1
        className="text-3xl font-normal text-black"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        We couldn't load the collection
      </h1>
      <p className="mt-4 text-sm text-black/60">
        The connection was interrupted while fetching our fragrances. Please try again.
      </p>
      <button
        onClick={() => {
          void router.invalidate();
          reset();
        }}
        className="mt-8 inline-block border border-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] transition-colors hover:border-[#C6A15B] hover:text-[#C6A15B]"
      >
        Try again
      </button>
    </section>
  );
}

function Index() {
  const { bestSellers, newArrivals, collections, imagery } = Route.useLoaderData();
  const best = useHomeSection("bestsellers");
  const fresh = useHomeSection("newarrivals");
  const bestLimit = Number(best.s("limit")) || 4;
  const freshLimit = Number(fresh.s("limit")) || 4;
  return (
    <>
      {/* Go-live announcement + promotional banner (Admin → Settings → Homepage / Promotions). */}
      <HomePromotions />
      <Hero image={imagery[0] ?? ""} />
      <ShopByVideo />
      <BrandStory image={imagery[2] ?? imagery[0] ?? ""} />
      <Collections collections={collections} />
      <ProductGrid
        eyebrow={best.s("eyebrow")}
        title={best.s("title")}
        description={best.s("description")}
        products={bestSellers.slice(0, bestLimit)}
        ctaLabel={best.s("ctaLabel")}
        ctaTo={best.s("ctaHref")}
        background="cream"
      />
      <ProductGrid
        eyebrow={fresh.s("eyebrow")}
        title={fresh.s("title")}
        description={fresh.s("description")}
        products={newArrivals.slice(0, freshLimit)}
        ctaLabel={fresh.s("ctaLabel")}
        ctaTo={fresh.s("ctaHref")}
      />
      <WhyCB16 />
      <Testimonials />
      <DiscoveryPacks />
      <JournalTeasers />
      <CallToAction image={imagery[1] ?? imagery[0] ?? ""} />
      <Newsletter />
      <SocialShowcase images={imagery} />
    </>
  );
}
